import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError, of } from "rxjs";
import { switchMap, catchError } from "rxjs/operators";
import { environment as ENV } from "@environments/environment";
import { EncryptionService } from "../../shared/services/encryption/encryption-service.service";
import { EXCLUDE_API_ENCRYPT } from "@app/shared/constant/api-url";

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {

  constructor(private readonly ecyService: EncryptionService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isGetReq = req.method == "GET";
    const reqMethod = req.method == "POST" || req.method == "PUT" || req.method == "DELETE" || isGetReq;
    const isToApi = req.url.includes(ENV.api);
    const isInBlackList = EXCLUDE_API_ENCRYPT.includes(req.url);
    if ((reqMethod && isToApi && !isInBlackList)) {
      return this.handleEncryptedRequest(req, next);
    } else {
      return this.handleRegularRequest(req, next);
    }
  }

  private handleEncryptedRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const locKey: string = this.ecyService.generateRandomKey();
    const cryLocKey: string = this.ecyService.encryptRSA(locKey);
    const cryData: string = this.ecyService.encryptHybridBody(req.body, locKey);
    const headers = {
      "key-sync-random": cryLocKey,
    };
    const clonedReq = req.clone({ body: { data: cryData }, setHeaders: {...headers} });
    return next.handle(clonedReq).pipe(
      switchMap((event:any) => {
        return this._processHybridResponse(event);
      }),
      catchError((error) =>
        this.handleHybridErrorResponse(error)
      )
    );
  }

  private decryptHybridResponseBody(
    event: HttpResponse<any>
  ): Observable<HttpEvent<any>>{
    const sanitized: any = this.ecyService.decryptHybridBody(event.body.data, event.url);
    let responseBody;
    if(sanitized?.data?.error){
      try {
        responseBody = JSON.parse(sanitized?.data?.error);
      } catch (error) {
        ENV.showLogs && console.log("Error no en json", error);
        responseBody = sanitized?.data?.error;
      }
      const clonedEvent = event.clone({ body: responseBody, error: responseBody, status: responseBody.status } as any);
      return throwError(() => clonedEvent);
    }
    if(typeof sanitized?.data == "string") responseBody = JSON.parse(sanitized?.data);
    responseBody = sanitized?.data ?? sanitized;
    const clonedEvent = event.clone({ body: responseBody });
    return of(clonedEvent);
  }

  private handleRegularRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      switchMap((event: HttpEvent<any>) => this.handleRegularResponse(event)),
      catchError((error: HttpErrorResponse) => this.handleErrorResponse(error))
    );
  }

  private handleRegularResponse(
    event: HttpEvent<any>
  ): Observable<HttpEvent<any>> {
    return this._processHybridResponse(event);
  }

  private handleHybridErrorResponse(error: any): Observable<never> {
    if (
      error.error?.data &&
      typeof error.error?.data === "string"
    ) {
      return this.ecyService.decryptHybridBody(error.error.data).pipe(
        switchMap((decryptedBody) => {
          const decryptedError = new HttpErrorResponse({
            ...error,
            error: { ...error.error, data: decryptedBody },
          });
          return throwError(() => decryptedError);
        }),
        catchError((decryptError) => {
          return throwError(() => decryptError);
        })
      );
    }
    return throwError(() => error);
  }

  private handleErrorResponse(error: HttpErrorResponse): Observable<never> {
    if (
      error.error?.data &&
      typeof error.error?.data === "string"
    ) {
      try {
        const decryptedError = new HttpErrorResponse({
          ...error,
          error: { ...error.error, data: error.error.data },
        });
        return throwError(() => decryptedError);
      } catch (error) {
        return throwError(() => error.error.data);
      }
    }
    return throwError(() => error);
  }

  private _processHybridResponse(event: HttpEvent<any>): Observable<HttpEvent<any>> {
    if (
      event instanceof HttpResponse &&
      event.body &&
      typeof event.body.data === "string"
    ) {
      return this.decryptHybridResponseBody(event);
    }
    return of(event);
  }

}
